import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import RecordGrid from "./components/RecordGrid";
import NowPlaying from "./components/NowPlaying";
import { cleanArtistName } from "./utils/formatters";
import "./Records.css";

const DISCOGS_API_URL = `https://api.discogs.com/users/rjbogz/collection/folders/0/releases?token=${process.env.REACT_APP_DISCOG_TOKEN}&per_page=250&sort=artist`;
const API_BASE_URL = "https://rjbogz.com/api";
const DEFAULT_COVER = "https://rjbogz.com/records/default.png";
const TRACK_CACHE_KEY = "discogs-track-cache-v1";

const resolveCoverUrl = (cover) => {
    if (!cover) return DEFAULT_COVER;
    if (cover.startsWith("http://") || cover.startsWith("https://")) return cover;
    return `https://rjbogz.com/records/${cover}`;
};

const Records = () => {
    const [recordData, setRecordData] = useState({});
    const [discogsData, setDiscogsData] = useState({});
    const [nowPlaying, setNowPlaying] = useState(null);

    useEffect(() => {
        const fetchRecords = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/records`);
                if (!response.ok) throw new Error("Failed to fetch records");

                const data = await response.json();
                setRecordData(data || {});
            } catch (error) {
                console.error("Error fetching records:", error);
            }
        };

        fetchRecords();
    }, []);

    useEffect(() => {
        if (!Object.keys(recordData).length) {
            setDiscogsData({});
            return;
        }

        const fetchDiscogsData = async () => {
            try {
                const wantedIds = new Set(
                    Object.values(recordData)
                        .map((record) => String(record.discogs_id || ""))
                        .filter(Boolean)
                );

                let page = 1;
                let totalPages = 1;
                const allReleases = [];

                while (page <= totalPages) {
                    const response = await fetch(`${DISCOGS_API_URL}&page=${page}`);
                    if (!response.ok) throw new Error("Failed to fetch Discogs data");
                    const data = await response.json();

                    totalPages = data.pagination && data.pagination.pages ? data.pagination.pages : 1;
                    allReleases.push(...(data.releases || []));
                    page += 1;
                }

                let cachedDetails = {};
                try {
                    cachedDetails = JSON.parse(localStorage.getItem(TRACK_CACHE_KEY) || "{}");
                } catch (error) {
                    cachedDetails = {};
                }

                let cacheUpdated = false;
                const mappedData = {};

                for (const release of allReleases) {
                    const basicInfo = release.basic_information || {};
                    const labels = basicInfo.labels || [];
                    const releaseId = String(basicInfo.id || release.id);

                    if (!wantedIds.has(releaseId)) continue;

                    let releaseDetails = cachedDetails[releaseId];

                    if (!releaseDetails) {
                        try {
                            const detailsResponse = await fetch(
                                `https://api.discogs.com/releases/${releaseId}?token=${process.env.REACT_APP_DISCOG_TOKEN}`
                            );

                            if (detailsResponse.ok) {
                                const details = await detailsResponse.json();

                                const trackList = (details.tracklist || []).map((track) => {
                                    const matchingVideo = (details.videos || []).find((video) =>
                                        (video.title || "")
                                            .toLowerCase()
                                            .includes((track.title || "").toLowerCase())
                                    );

                                    return {
                                        title: track.title,
                                        duration: track.duration || "N/A",
                                        position: track.position || "",
                                        youtubeUrl: (matchingVideo && matchingVideo.uri) ? matchingVideo.uri : null,
                                    };
                                });

                                releaseDetails = {
                                    trackNames: trackList.map((track) => track.title).filter(Boolean),
                                    trackList,
                                };

                                cachedDetails[releaseId] = releaseDetails;
                                cacheUpdated = true;
                            }
                        } catch (error) {
                            console.error(`Error fetching release details for ${releaseId}:`, error);
                        }
                    }

                    mappedData[releaseId] = {
                        album: basicInfo.title || "Unknown Album",
                        artist:
                            (basicInfo.artists &&
                                basicInfo.artists.length > 0 &&
                                basicInfo.artists[0] &&
                                basicInfo.artists[0].name) ||
                            "Unknown Artist",
                        cover: basicInfo.cover_image || DEFAULT_COVER,
                        labels: labels.length > 0 ? labels.map((label) => label.name).join(", ") : "N/A",
                        trackNames: (releaseDetails && releaseDetails.trackNames) ? releaseDetails.trackNames : [],
                        trackList: (releaseDetails && releaseDetails.trackList) ? releaseDetails.trackList : [],
                        discogs_id: releaseId,
                    };
                }

                if (cacheUpdated) {
                    localStorage.setItem(TRACK_CACHE_KEY, JSON.stringify(cachedDetails));
                }

                setDiscogsData(mappedData);
            } catch (error) {
                console.error("Error fetching Discogs data:", error);
            }
        };

        fetchDiscogsData();
    }, [recordData]);

    useEffect(() => {
        const fetchRecordEvent = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/currently-playing`);
                if (!response.ok) throw new Error("Failed to fetch currently playing record");

                const currentlyPlaying = await response.json();
                console.log(currentlyPlaying);

                if (
                    currentlyPlaying.playing === false ||
                    currentlyPlaying.status === "stopped" ||
                    currentlyPlaying.idle
                ) {
                    setNowPlaying(null);
                    return;
                }

                if (currentlyPlaying.album) {
                    const matchedRecord =
                        (currentlyPlaying.record && recordData[currentlyPlaying.record]) ||
                        Object.values(recordData).find(
                            (record) =>
                                currentlyPlaying.discogs_id &&
                                String(record.discogs_id || "") === String(currentlyPlaying.discogs_id)
                        ) ||
                        Object.values(recordData).find(
                            (record) =>
                                record.album === currentlyPlaying.album &&
                                (!currentlyPlaying.artist || record.artist === currentlyPlaying.artist)
                        );

                    const recordInfo = matchedRecord || {
                        album: currentlyPlaying.album,
                        artist: cleanArtistName(currentlyPlaying.artist || "Unknown Artist"),
                        cover: currentlyPlaying.cover || DEFAULT_COVER,
                        discogs_id: currentlyPlaying.discogs_id || null,
                    };

                    setNowPlaying({
                        record: currentlyPlaying.record || currentlyPlaying.album,
                        album: recordInfo.album,
                        artist: cleanArtistName(recordInfo.artist || "Unknown Artist"),
                        cover: resolveCoverUrl(recordInfo.cover),
                        streamUrl: `${API_BASE_URL}/stream`,
                        status: "playing",
                    });
                    return;
                }

                setNowPlaying(null);
            } catch (error) {
                console.error("Error fetching currently playing record:", error);
                setNowPlaying(null);
            }
        };

        fetchRecordEvent();

        const interval = setInterval(fetchRecordEvent, 2000);
        return () => clearInterval(interval);
    }, [recordData]);

    const handlePlay = () => {
        setNowPlaying((prev) => ({ ...prev, status: "playing" }));
        const audio = new Audio(nowPlaying.streamUrl);
        audio.play();
    };

    const handleStop = () => {
        setNowPlaying((prev) => ({ ...prev, status: "stopped" }));
    };

    const handleResume = () => {
        setNowPlaying((prev) => ({ ...prev, status: "playing" }));
        const audio = new Audio(nowPlaying.streamUrl);
        audio.play();
    };

    return (
        <div className="Records">
            <Helmet>
                <title>Records</title>
                <link rel="canonical" href="https://rjbogz.com/records" />
                <link rel="manifest" href="/records-manifest.json" />
                <link rel="icon" type="image/x-icon" href="/records-icon.ico" />
                <link rel="apple-touch-icon" href="/records-icon.ico" />
                <meta name="apple-mobile-web-app-capable" content="yes" />
                <meta name="mobile-web-app-capable" content="yes" />
                <meta name="apple-mobile-web-app-title" content="Records" />
                <meta name="application-name" content="Records" />
            </Helmet>

            <header>
                <h1>Record Collection</h1>
            </header>

            {nowPlaying && (
                <NowPlaying
                    nowPlaying={nowPlaying}
                    onPlay={handlePlay}
                    onStop={handleStop}
                    onResume={handleResume}
                />
            )}

            <RecordGrid
                records={recordData}
                discogsData={discogsData}
                onNowPlaying={setNowPlaying}
            />
        </div>
    );
};

export default Records;
